import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, useNavigate, Link, useParams } from '@tanstack/react-router';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { cn } from '@/lib/utils';
import { FileQuestion, CheckCircle, XCircle, Award } from 'lucide-react';
import { certificationService } from '@/features/certification/services/certification.service';
import { usePermissions } from '@/common/hooks/usePermissions';
import { Permission } from '@/common/enums/permissions.enum';
import { useToast } from '@/hooks/use-toast';

function SimuladoPage() {
  const { courseId } = useParams({ from: '/_authenticated/certification/courses/$courseId/simulado' });
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { hasPermission } = usePermissions();
  const canView = hasPermission(Permission.CERTIFICATION_VIEW);
  const { toast } = useToast();

  const [attemptId, setAttemptId] = useState<string | null>(null);
  const [questions, setQuestions] = useState<
    Array<{ id: string; stem: string; options: string[]; type: string }>
  >([]);
  const [answers, setAnswers] = useState<Record<string, number>>({});
  const [result, setResult] = useState<{
    scorePct: number;
    passed: boolean;
    correct: number;
    total: number;
    nextAttemptAllowedAt: string;
  } | null>(null);

  const { data: certificate } = useQuery({
    queryKey: ['certification', 'certificate', courseId],
    queryFn: () => certificationService.getCertificateByCourse(courseId),
    enabled: !!result?.passed,
  });

  const { data: cooldown, isLoading: cooldownLoading } = useQuery({
    queryKey: ['certification', 'cooldown', courseId],
    queryFn: () => certificationService.getCooldown(courseId),
    enabled: canView && !!courseId && !attemptId && !result,
  });

  const startMutation = useMutation({
    mutationFn: () => certificationService.startSimulator(courseId),
    onSuccess: (data) => {
      setAttemptId(data.attemptId);
      setQuestions(data.questions);
      setAnswers({});
      setResult(null);
    },
    onError: (error: any) => {
      toast({
        title: 'Erro',
        description: error?.response?.data?.message || 'Não foi possível iniciar o simulado. Verifique se há questões configuradas.',
        variant: 'destructive',
      });
    },
  });

  const submitMutation = useMutation({
    mutationFn: () => {
      if (!attemptId) throw new Error('Sem tentativa ativa');
      return certificationService.submitSimulator(
        courseId,
        attemptId,
        Object.entries(answers).map(([questionId, answerIndex]) => ({
          questionId,
          answerIndex,
        })),
      );
    },
    onSuccess: (data) => {
      setResult(data);
      queryClient.invalidateQueries({ queryKey: ['certification', 'certificate', courseId] });
      queryClient.invalidateQueries({ queryKey: ['certification', 'progress', courseId] });
    },
  });

  const nextAllowed = cooldown?.nextAttemptAllowedAt
    ? new Date(cooldown.nextAttemptAllowedAt)
    : null;
  const canStart =
    !cooldownLoading &&
    !nextAllowed &&
    !startMutation.isPending &&
    !attemptId &&
    !result;

  if (!canView) {
    return (
      <div className="p-6">
        <p className="text-muted-foreground">Sem permissão.</p>
      </div>
    );
  }

  const showLoading = cooldownLoading && !result && !attemptId;
  const showResult = !!result;
  const showStartCard = !result && !attemptId && !cooldownLoading;
  const showQuestions = !result && attemptId && questions.length > 0;
  const hasAnyContent = showLoading || showResult || showStartCard || showQuestions;

  return (
    <div className="p-6 max-w-2xl mx-auto space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold">Simulado</h1>
        <Link
          to="/certification/courses/$courseId"
          params={{ courseId }}
          className="text-sm text-muted-foreground hover:underline"
        >
          Voltar ao curso
        </Link>
      </div>

      {!hasAnyContent && (
        <Card>
          <CardContent className="pt-6">
            <div className="text-center text-muted-foreground">
              <p>Preparando simulado...</p>
            </div>
          </CardContent>
        </Card>
      )}

      {showLoading && (
        <Card>
          <CardContent className="pt-6">
            <div className="text-center text-muted-foreground">
              <p>Carregando...</p>
            </div>
          </CardContent>
        </Card>
      )}

      {showResult && (
        <Card>
          <CardHeader>
            {result.passed ? (
              <div className="flex items-center gap-2 text-green-600">
                <CheckCircle className="h-6 w-6" />
                <span className="font-semibold">Aprovado!</span>
              </div>
            ) : (
              <div className="flex items-center gap-2 text-destructive">
                <XCircle className="h-6 w-6" />
                <span className="font-semibold">Reprovado</span>
              </div>
            )}
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-1">
              <p className="text-lg font-semibold">
                Pontuação: {result.correct}/{result.total} questões corretas
              </p>
              <p className="text-sm text-muted-foreground">
                ({result.scorePct}% de acerto)
              </p>
            </div>
            {!result.passed && (
              <Alert>
                <AlertDescription>
                  Você pode tentar novamente em{' '}
                  {new Date(result.nextAttemptAllowedAt).toLocaleDateString('pt-BR', {
                    day: '2-digit',
                    month: '2-digit',
                    year: 'numeric',
                  })}
                </AlertDescription>
              </Alert>
            )}
            {result.passed && certificate && (
              <Button
                onClick={() =>
                  navigate({
                    to: '/certification/certificados/$certificateId',
                    params: { certificateId: certificate.id },
                  })
                }
                className="w-full"
              >
                <Award className="h-4 w-4 mr-2" />
                Ver certificado
              </Button>
            )}
            <Button
              onClick={() =>
                navigate({ to: '/certification/courses/$courseId', params: { courseId } })
              }
              variant={result.passed && certificate ? 'outline' : 'default'}
              className="w-full"
            >
              Voltar ao curso
            </Button>
          </CardContent>
        </Card>
      )}

      {showStartCard && (
          <Card>
            <CardContent className="pt-6">
              {nextAllowed && (
                <Alert className="mb-4">
                  <AlertDescription>
                    Nova tentativa permitida a partir de{' '}
                    {nextAllowed.toLocaleDateString('pt-BR', {
                      day: '2-digit',
                      month: '2-digit',
                      year: 'numeric',
                    })}
                  </AlertDescription>
                </Alert>
              )}
              <Button
                onClick={() => startMutation.mutate()}
                disabled={!canStart || startMutation.isPending}
                className="w-full"
              >
                <FileQuestion className="h-4 w-4 mr-2" />
                {startMutation.isPending ? 'Iniciando...' : 'Iniciar simulado'}
              </Button>
              {!canStart && (
                <p className="text-xs text-muted-foreground mt-2 text-center">
                  {nextAllowed && `Aguarde até ${nextAllowed.toLocaleDateString('pt-BR')}`}
                  {!nextAllowed && 'Não é possível iniciar o simulado no momento'}
                </p>
              )}
            </CardContent>
          </Card>
      )}

      {showQuestions && (
        <Card>
          <CardHeader>
            <h2 className="font-semibold">Questões</h2>
            <p className="text-sm text-muted-foreground">
              Responda todas as questões antes de enviar
            </p>
          </CardHeader>
          <CardContent className="space-y-6">
            {questions.map((q, idx) => (
              <div key={q.id} className="space-y-3">
                <p className="font-medium">
                  {idx + 1}. {q.stem}
                </p>
                <div className="space-y-2">
                  {q.options.map((opt, i) => (
                    <button
                      key={i}
                      type="button"
                      onClick={() =>
                        setAnswers((prev) => ({ ...prev, [q.id]: i }))
                      }
                      className={cn(
                        'w-full text-left px-4 py-2 rounded-md border text-sm transition-colors',
                        answers[q.id] === i
                          ? 'border-primary bg-primary/10'
                          : 'border-input hover:bg-muted/50',
                      )}
                    >
                      <div className="flex items-center gap-2">
                        <div
                          className={cn(
                            'h-5 w-5 rounded-full border-2 shrink-0',
                            answers[q.id] === i
                              ? 'border-primary bg-primary'
                              : 'border-muted-foreground',
                          )}
                        />
                        <span className="flex-1">{opt}</span>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            ))}
            <Button
              className="w-full"
              onClick={() => submitMutation.mutate()}
              disabled={
                submitMutation.isPending ||
                Object.keys(answers).length !== questions.length
              }
            >
              {submitMutation.isPending ? 'Enviando...' : 'Enviar respostas'}
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

export const Route = createFileRoute(
  '/_authenticated/certification/courses/$courseId/simulado',
)({
  component: SimuladoPage,
});
